import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'timer:presets'
const MAX_PRESETS = 4

export interface TimerPreset {
	rounds: number
	approach: number
	rest: number
	totalSeconds: number
}

export const buildTimerPresetId = (preset: TimerPreset) =>
	`${preset.rounds}-${preset.approach}-${preset.rest}-${preset.totalSeconds}`

export const parseTimerPresetId = (value: string): TimerPreset | null => {
	if (!value) {
		return null
	}

	const [roundsPart, approachPart, restPart, totalPart] = value.split('-')
	const rounds = Number(roundsPart)
	const approach = Number(approachPart)
	const rest = Number(restPart)
	const parsedTotalSeconds = Number(totalPart)
	const totalSeconds = Number.isNaN(parsedTotalSeconds)
		? rounds * approach + rounds * rest
		: parsedTotalSeconds

	if ([rounds, approach, rest, totalSeconds].some(Number.isNaN)) {
		return null
	}

	return { rounds, approach, rest, totalSeconds }
}

const isTimerPreset = (value: unknown): value is TimerPreset => {
	if (typeof value !== 'object' || value === null) {
		return false
	}

	const candidate = value as Partial<TimerPreset>
	return (
		typeof candidate.rounds === 'number' &&
		typeof candidate.approach === 'number' &&
		typeof candidate.rest === 'number' &&
		typeof candidate.totalSeconds === 'number'
	)
}

const readPresets = (): TimerPreset[] => {
	if (typeof window === 'undefined') {
		return []
	}

	try {
		const stored = window.localStorage.getItem(STORAGE_KEY)
		if (!stored) {
			return []
		}

		const parsed = JSON.parse(stored)
		if (!Array.isArray(parsed)) {
			return []
		}

		return parsed.filter(isTimerPreset)
	} catch {
		return []
	}
}

export const areTimerPresetsEqual = (lhs: TimerPreset, rhs: TimerPreset) =>
	lhs.rounds === rhs.rounds &&
	lhs.approach === rhs.approach &&
	lhs.rest === rhs.rest &&
	lhs.totalSeconds === rhs.totalSeconds

export const useTimerPresets = () => {
	const [presets, setPresets] = useState<TimerPreset[]>([])

	useEffect(() => {
		if (typeof window === 'undefined') {
			return
		}

		setPresets(readPresets())
	}, [])

	const savePreset = useCallback((preset: TimerPreset) => {
		if (typeof window === 'undefined') {
			return
		}

		setPresets(prev => {
			const filtered = prev.filter(item => !areTimerPresetsEqual(item, preset))
			const next = [preset, ...filtered].slice(0, MAX_PRESETS)
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
			return next
		})
	}, [])

	const removePreset = useCallback((preset: TimerPreset) => {
		if (typeof window === 'undefined') {
			return
		}

		setPresets(prev => {
			const next = prev.filter(item => !areTimerPresetsEqual(item, preset))
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
			return next
		})
	}, [])

	return { presets, savePreset, removePreset }
}
